import { useEffect, useState } from "react";
import { ExternalLink, KeyRound, Loader2, RefreshCw, Search, Star, Unlink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const errMsg = (err, fallback) => { const d = err.response?.data?.detail; return typeof d === "string" ? d : fallback; };

export function GoogleReviewsAdmin() {
  const [state, setState] = useState(null);
  const [q, setQ] = useState("Twoje Biuro Domaniewska 17/19 Warszawa");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [editingKey, setEditingKey] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setState(r.data)).catch(() => toast.error("Nie udało się pobrać ustawień opinii."));
  useEffect(() => { load(); }, []);
  if (!state) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-600" /></div>;

  const saveKey = async () => {
    if (!keyInput.trim()) { toast.error("Wklej klucz API Google Places."); return; }
    setSavingKey(true);
    try {
      await api.put("/admin/integrations", { group: "google", values: { places_api_key: keyInput.trim() }, clears: [] });
      toast.success("Klucz Google Places API zapisany.");
      setKeyInput(""); setEditingKey(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Nie udało się zapisać klucza."));
    } finally {
      setSavingKey(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Usunąć klucz Google Places API z bazy? Pobieranie opinii przestanie działać (chyba że klucz jest w pliku .env).")) return;
    setSavingKey(true);
    try {
      await api.put("/admin/integrations", { group: "google", values: {}, clears: ["places_api_key"] });
      toast.success("Klucz usunięty z bazy.");
      setEditingKey(false); setKeyInput("");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Nie udało się usunąć klucza."));
    } finally {
      setSavingKey(false);
    }
  };

  const search = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/reviews/search", { params: { q } });
      setResults(r.data.places);
      if (!r.data.places.length) toast.info("Nie znaleziono wizytówki — spróbuj innej nazwy.");
    } catch (err) {
      toast.error(errMsg(err, "Wyszukiwanie nie powiodło się."));
    } finally {
      setBusy(false);
    }
  };
  const pick = async (p) => {
    setBusy(true);
    try {
      await api.put("/admin/reviews/place", { place_id: p.place_id, name: p.name, address: p.address });
      toast.success("Wizytówka połączona — opinie Google pobrane.");
      setResults(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Nie udało się pobrać opinii."));
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reviews/refresh");
      toast.success("Opinie odświeżone.");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Odświeżenie nie powiodło się."));
    } finally {
      setBusy(false);
    }
  };
  const unlink = async () => {
    if (!window.confirm("Odłączyć wizytówkę Google? Strona wróci do opinii wpisanych ręcznie.")) return;
    await api.put("/admin/reviews/place", { place_id: "" });
    toast.success("Wizytówka odłączona.");
    load();
  };

  const d = state.data;
  return (
    <div data-testid="google-reviews-admin" className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
      <div>
        <h3 className="font-display font-bold text-lg">Opinie z Google</h3>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          Po połączeniu wizytówki sekcja „Opinie” na stronie pokazuje prawdziwe opinie z Google (do 5, odświeżane raz dziennie) wraz z łączną oceną i liczbą opinii.
          Opinie wpisane ręcznie poniżej są wtedy pomijane (pozostają jako zapas).
        </p>
      </div>

      <div data-testid="google-reviews-key-config" className="rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <span className="font-semibold inline-flex items-center gap-1.5"><KeyRound size={14} className="text-brand-teal" /> Klucz Google Places API</span>
            {state.key_set
              ? <span data-testid="google-reviews-key-status" className="ml-2 text-xs text-brand-teal">skonfigurowany ✓</span>
              : <span data-testid="google-reviews-key-status" className="ml-2 text-xs text-destructive">brak — dodaj, aby pobierać opinie</span>}
          </div>
          {state.key_set && !editingKey && (
            <div className="flex gap-2">
              <button type="button" data-testid="google-reviews-key-edit" onClick={() => setEditingKey(true)} className={`${btn} border border-border hover:bg-muted`}>Zmień klucz</button>
              <button type="button" data-testid="google-reviews-key-clear" onClick={clearKey} disabled={savingKey} className={`${btn} text-destructive hover:bg-destructive/10`}>Usuń klucz</button>
            </div>
          )}
        </div>
        {(!state.key_set || editingKey) && (
          <div className="flex gap-2 flex-wrap">
            <Input data-testid="google-reviews-key-input" type="password" autoComplete="new-password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="Wklej klucz API Google Places" className="bg-background flex-1 min-w-[220px]" onKeyDown={(e) => e.key === "Enter" && saveKey()} />
            <button type="button" data-testid="google-reviews-key-save" onClick={saveKey} disabled={savingKey} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark shrink-0`}>
              {savingKey ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Zapisz klucz
            </button>
            {editingKey && <button type="button" data-testid="google-reviews-key-cancel" onClick={() => { setEditingKey(false); setKeyInput(""); }} className={`${btn} border border-border hover:bg-muted`}>Anuluj</button>}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground leading-relaxed">Klucz jest szyfrowany w bazie (AES/Fernet) i używany wyłącznie do pobierania opinii z Google. Ten sam klucz można też ustawić w zakładce „Integracje”.</p>
      </div>

      {state.place?.place_id ? (
        <div data-testid="google-reviews-linked" className="rounded-xl border border-border p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-semibold">{state.place.name || d?.name}</div>
              <div className="text-xs text-muted-foreground">{state.place.address}</div>
              <code className="text-[10px] text-muted-foreground">{state.place.place_id}</code>
            </div>
            {d && (
              <div className="text-right">
                <div className="font-display font-bold text-2xl inline-flex items-center gap-1"><Star size={18} className="fill-brand-yellow text-brand-yellow" /> {d.rating}</div>
                <div className="text-xs text-muted-foreground">{d.count} opinii · {d.reviews?.length || 0} pobranych</div>
                <div className="text-[10px] text-muted-foreground">odświeżono {d.fetched_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="google-reviews-refresh" onClick={refresh} disabled={busy} className={`${btn} border border-border hover:bg-muted`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Odśwież teraz
            </button>
            {d?.maps_uri && <a href={d.maps_uri} target="_blank" rel="noopener noreferrer" className={`${btn} border border-border hover:bg-muted`}><ExternalLink size={13} /> Otwórz w Google Maps</a>}
            <button type="button" data-testid="google-reviews-unlink" onClick={unlink} className={`${btn} text-destructive hover:bg-destructive/10`}><Unlink size={13} /> Odłącz</button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input data-testid="google-reviews-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nazwa firmy i adres" className="bg-background" onKeyDown={(e) => e.key === "Enter" && search()} />
            <button type="button" data-testid="google-reviews-search-button" onClick={search} disabled={busy || !state.key_set} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark shrink-0`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Znajdź wizytówkę
            </button>
          </div>
          {results && results.map((p) => (
            <button key={p.place_id} type="button" data-testid={`google-reviews-result-${p.place_id}`} onClick={() => pick(p)} disabled={busy}
              className="w-full text-left rounded-xl border border-border p-3 hover:bg-muted transition-colors">
              <div className="font-semibold text-sm">{p.name} {p.rating && <span className="text-xs text-muted-foreground font-normal ml-2">★ {p.rating} · {p.count} opinii</span>}</div>
              <div className="text-xs text-muted-foreground">{p.address}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
